import { parseFrac, quantizeTo24, countLineQuantity } from "utils/ingredientFormatting";

// Units whose per-recipe display already snaps to the COOK_FRACS cooking-
// fraction menu (see ingredientFormatting.roundQty). When several recipes
// contribute the same ingredient in one of these units, the aggregate must
// sum the *quantized* 1/24ths each recipe's card actually displays — not
// the raw exact decimal — otherwise the total is rounded independently
// from a different (invisible) number and can land on a fraction that
// doesn't match what a shopper gets by hand-adding the visible cards.
const FRACTION_UNITS = new Set(["lb", "oz", "cup", "tbsp", "tsp"]);

/**
 * The exact 1/24ths one contribution *displays* on its per-recipe card, or
 * null when its unit has no display rounding to reconcile against (metric
 * amounts, unrecognized units) and the raw quantity is summed as before.
 *
 * The whole point of this is that the consolidated total has to equal the sum
 * of the visible parts. Count-type rows are the case that was missing: they
 * round UP (you can't buy 2/3 of a clove), so summing the exact decimals and
 * rounding once at the end lands a whole piece below the lines a shopper adds
 * up by hand — garlic listed as 3 + 3 + 3 cloves per recipe and 8 in total.
 * 1/24ths are exact for every fraction either menu can produce, so these sums
 * carry no float drift and need no second rounding.
 */
function displayed24(ing, unitLower) {
  if (FRACTION_UNITS.has(unitLower)) return quantizeTo24(parseFrac(ing.quantity));
  if (unitLower === "") return Math.round(countLineQuantity(ing) * 24);
  return null;
}

export const CATEGORIES = [
  {
    key: "produce",
    label: "Produce",
    emoji: "🥦",
    color: "#d4edda",
    border: "#7cb98a",
    keywords: [
      "spinach",
      "lettuce",
      "cabbage",
      "kale",
      "arugula",
      "chard",
      "collard",
      "bok choy",
      "tomato",
      "pepper",
      "bell pepper",
      "zucchini",
      "eggplant",
      "broccoli",
      "cauliflower",
      "asparagus",
      "artichoke",
      "celery",
      "leek",
      "fennel",
      "corn",
      "pea",
      "green bean",
      "bean sprout",
      "onion",
      "red onion",
      "green onion",
      "scallion",
      "shallot",
      "garlic",
      "ginger",
      "potato",
      "sweet potato",
      "yam",
      "carrot",
      "beet",
      "radish",
      "turnip",
      "parsnip",
      "cucumber",
      "mushroom",
      "avocado",
      "lemon",
      "lime",
      "orange",
      "apple",
      "pear",
      "berry",
      "grape",
      "cherry",
      "plum",
      "peach",
      "mango",
      "pineapple",
      "banana",
      "fig",
      "date",
      "fresh herb",
      "fresh parsley",
      "fresh dill",
      "fresh mint",
      "fresh basil",
      "fresh cilantro",
      "fresh thyme",
      "fresh rosemary",
      "fresh sage",
      "fresh chive",
    ],
  },
  {
    key: "meat",
    label: "Meat & Seafood",
    emoji: "🥩",
    color: "#ffe0e0",
    border: "#d08080",
    keywords: [
      "chicken",
      "turkey",
      "beef",
      "pork",
      "lamb",
      "veal",
      "duck",
      "bacon",
      "sausage",
      "ham",
      "ground beef",
      "ground turkey",
      "ground pork",
      "steak",
      "sirloin",
      "tenderloin",
      "rib",
      "chorizo",
      "prosciutto",
      "pancetta",
      "salami",
      "pepperoni",
      "fish",
      "salmon",
      "tuna",
      "cod",
      "tilapia",
      "catfish",
      "halibut",
      "trout",
      "sea bass",
      "shrimp",
      "prawn",
      "crab",
      "lobster",
      "scallop",
      "clam",
      "mussel",
      "squid",
      "octopus",
      "anchov",
    ],
  },
  {
    key: "dairy",
    label: "Dairy & Eggs",
    emoji: "🥛",
    color: "#e2d9f7",
    border: "#9e82d0",
    keywords: [
      "milk",
      "cream",
      "half and half",
      "butter",
      "margarine",
      "ghee",
      "cheese",
      "parmesan",
      "mozzarella",
      "cheddar",
      "feta",
      "ricotta",
      "gouda",
      "brie",
      "camembert",
      "cottage cheese",
      "cream cheese",
      "goat cheese",
      "swiss cheese",
      "gruyere",
      "provolone",
      "yogurt",
      "sour cream",
      "creme fraiche",
      "kefir",
      "egg",
      "egg white",
      "egg yolk",
    ],
  },
  {
    key: "drygoods",
    label: "Dry Goods & Cans",
    emoji: "🥫",
    color: "#faedc8",
    border: "#c8a444",
    keywords: [
      "pasta",
      "spaghetti",
      "penne",
      "rigatoni",
      "rotini",
      "fettuccine",
      "linguine",
      "tagliatelle",
      "lasagna",
      "orzo",
      "couscous",
      "noodle",
      "ramen",
      "udon",
      "soba",
      "rice",
      "long grain rice",
      "basmati",
      "jasmine",
      "arborio",
      "risotto",
      "brown rice",
      "wild rice",
      "bread",
      "breadcrumb",
      "crouton",
      "tortilla",
      "pita",
      "naan",
      "baguette",
      "flour",
      "cornmeal",
      "polenta",
      "oat",
      "oatmeal",
      "granola",
      "cereal",
      "quinoa",
      "farro",
      "barley",
      "bulgur",
      "millet",
      "amaranth",
      "buckwheat",
      "phyllo",
      "puff pastry",
      "pastry",
      "dough",
      "wrap",
      "canned",
      "jarred",
      "tomato paste",
      "crushed tomato",
      "diced tomato",
      "plum tomato",
      "tomato puree",
      "bean",
      "chickpea",
      "garbanzo",
      "lentil",
      "black bean",
      "kidney bean",
      "navy bean",
      "great northern bean",
      "cannellini",
      "pinto bean",
      "white bean",
      "corn kernel",
      "artichoke heart",
      "kalamata olive",
      "black olive",
      "green olive",
      "sardine",
      "tuna can",
      "roasted pepper",
      "sun-dried tomato",
      "sugar",
      "brown sugar",
      "honey",
      "maple syrup",
      "baking powder",
      "baking soda",
      "yeast",
      "cornstarch",
      "tapioca starch",
      "chocolate",
      "cocoa",
      "nut",
      "almond",
      "walnut",
      "pecan",
      "cashew",
      "pistachio",
      "hazelnut",
      "pine nut",
      "sesame seed",
      "raisin",
      "dried fruit",
    ],
  },
];

export const OTHER_KEY = "other";

// Grams per single count item, from ingredient_units.json g_per_unit field.
// Used to merge count-based quantities (e.g. "1 onion") with weight-based ones (e.g. "100g onion").
export const G_PER_UNIT = {
  artichoke: 120,
  avocado: 150,
  cabbage: 900,
  carrot: 80,
  celery: 40,
  cucumber: 200,
  egg: 50,
  "egg white": 33,
  "egg yolk": 17,
  garlic: 5,
  "green cabbage": 900,
  "green onion": 15,
  "green pepper": 150,
  "iceberg lettuce": 600,
  lemon: 90,
  lime: 65,
  onion: 110,
  "plum tomato": 90,
  "red bell pepper": 150,
  "red onion": 110,
  "romaine lettuce": 10, // per leaf — recipes reference romaine by the leaf, not by head
  scallion: 15,
  shallot: 30,
  tomato: 120,
};

// Unit strings that are all synonyms for "one whole item, counted" — different
// recipes/LLM ingestion batches phrase the same countable produce as "1 leaf",
// "1 leaf of", "1 piece", "1 head", etc. Rather than growing G_PER_UNIT to cover
// every phrasing (which conflates unrelated quantity semantics — a "leaf" and a
// "head" of the same vegetable are wildly different masses), normalize all of
// these to the app's existing count-unit convention (empty string, as used for
// "1 onion") BEFORE the aggregation key is built, so near-duplicate phrasing of
// the same countable ingredient merges via the ordinary same-unit sum path
// instead of ever reaching gram conversion.
const COUNT_UNIT_SYNONYMS = new Set([
  "leaf",
  "leaf of",
  "leaves",
  "leaves of",
  "piece",
  "piece of",
  "pieces",
  "pieces of",
  "head",
  "head of",
  "heads",
  "heads of",
  "clove",
  "clove of",
  "cloves",
  "cloves of",
  "slice",
  "slice of",
  "slices",
  "slices of",
  "sprig",
  "sprig of",
  "sprigs",
  "sprigs of",
  "stalk",
  "stalk of",
  "stalks",
  "stalks of",
  "whole",
  "each",
]);

function normalizeCountUnit(unit) {
  const u = (unit || "").trim().toLowerCase();
  return COUNT_UNIT_SYNONYMS.has(u) ? "" : u;
}

// Density table shared with the Python validator (ingredient_units.json).
// Only entries with density_g_per_cup are needed for cross-unit conversion.
export const DENSITY_G_PER_CUP = {
  "black olive": 135,
  butter: 227,
  cabbage: 90,
  "cherry tomato": 150,
  "cottage cheese": 226,
  crouton: 40,
  "dry curd cottage cheese": 226,
  "feta cheese": 113,
  flour: 125,
  "garbanzo bean": 164,
  "great northern bean": 180,
  "green olive": 135,
  "kalamata olive": 135,
  "long grain rice": 185,
  "mozzarella cheese": 115,
  mushroom: 70,
  "navy bean": 182,
  "ricotta cheese": 246,
};

export const MERGE_RULES = [
  [/^(red |white |yellow |sweet )?onion$/i, "onion"],
  [/^fresh\s+lemon\s+juice$/i, "lemon juice"],
  [/^chicken\s+broth$/i, "chicken stock"],
  [/^lettuce$/i, "romaine lettuce"],
  [/^(extra\s+virgin\s+|pure\s+|light\s+)?olive\s+oil$/i, "extra virgin olive oil"],
];

// For these canonical keys, always use this display name (ignores whichever DB variant appeared first)
export const MERGE_PREFERRED_DISPLAY = {
  "romaine lettuce": "Romaine Lettuce",
  "extra virgin olive oil": "Extra Virgin Olive Oil",
};

// Convert a quantity+unit to grams using the density and per-unit tables.
// Returns null if conversion is not possible.
export function toGrams(qty, unit, ingredientName) {
  const n = Number(qty);
  if (isNaN(n) || n === 0) return null;
  const u = (unit || "").toLowerCase();
  const name = ingredientName?.toLowerCase();
  const density = DENSITY_G_PER_CUP[name];
  if (u === "g") return n;
  if (u === "kg") return n * 1000;
  if (u === "oz") return n * 28.35;
  if (u === "lb") return n * 453.6;
  if (u === "cup" && density) return n * density;
  if (u === "tbsp" && density) return (n / 16) * density;
  if (u === "tsp" && density) return (n / 48) * density;
  if (u === "" && name && G_PER_UNIT[name]) return n * G_PER_UNIT[name];
  return null;
}

/**
 * Names that are real recipe ingredients but not things anybody buys.
 *
 * Water is an `Ingredient` rather than a pantry staple on purpose, and must
 * stay one: the cooking-liquid rule in normalize_batch.py rejects a step that
 * says "add the water" without a reachable quantity, and that check needs a
 * counted row to read (see CLAUDE.md). Correct for the recipe, wrong for the
 * shop — "1 tbsp water" filed under Other is a line a shopper has to read,
 * decide about and tick off, on every list. Dropped from the grocery view
 * only; the increments themselves are untouched, so every step still shows
 * its own quantity and the cooking-liquid gate still has something to read.
 */
export const NON_SHOPPING_NAME_RE = /^(cold |warm |hot |ice |boiling )?water$/i;

export function isShoppingItem(name) {
  return !NON_SHOPPING_NAME_RE.test((name || "").trim());
}

export function canonical(name) {
  const trimmed = (name || "").trim();
  for (const [pattern, target] of MERGE_RULES) {
    if (pattern.test(trimmed)) return target;
  }
  return trimmed.toLowerCase();
}

/**
 * Stable identity for one grocery line: canonical name + normalized source unit.
 *
 * This is the same composite `aggregate()` groups on, exposed so callers can
 * track per-item UI state (tick-off) against something that does not move.
 * It deliberately reads `unit` — the source US unit — not the display unit, and
 * ignores quantity entirely, so the key survives the metric toggle and any
 * servings rescale. Keying such state on the formatted label instead means it
 * is silently lost the moment either of those changes.
 */
export function ingredientKey(name, unit) {
  return `${canonical(name)}@@${normalizeCountUnit(unit)}`;
}

/**
 * Names whose aisle is decided by the whole phrase, not by a word inside it.
 *
 * `categorize` scans categories in order and takes the first keyword that
 * appears anywhere in the name, so a compound name is filed under whichever
 * of its words happens to be listed first. "chicken broth" is a carton off a
 * shelf, but it contains "chicken", and Meat & Seafood is scanned before Dry
 * Goods — so the grocery list sent shoppers to the butcher's counter for a
 * cup of stock. These are checked first, and each one has to be a phrase
 * (never a bare word that could appear inside an unrelated name).
 */
export const CATEGORY_OVERRIDES = [[/\b(broth|stock|bouillon|consomm(e|é))\b/i, "drygoods"]];

export function categorize(name) {
  const lower = name.toLowerCase();
  for (const [pattern, key] of CATEGORY_OVERRIDES) {
    if (pattern.test(lower)) return key;
  }
  for (const cat of CATEGORIES) {
    if (cat.keywords.some((kw) => lower.includes(kw))) return cat.key;
  }
  return OTHER_KEY;
}

export function groupByCategory(items) {
  const groups = {};
  items.forEach((item) => {
    const key = categorize(item.ingredient_name || "");
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });
  return groups;
}

export function aggregate(activeIngredients) {
  const map = {};
  const displayNames = {};
  activeIngredients.forEach(({ ingredients }) => {
    (ingredients || []).forEach((ing) => {
      if (!isShoppingItem(ing.ingredient_name)) return;
      const canon = canonical(ing.ingredient_name);
      const unitLower = normalizeCountUnit(ing.unit);
      const key = `${canon}@@${unitLower}`;
      const isSpecific = /red |white |fresh /i.test(ing.ingredient_name);
      if (!displayNames[key] || isSpecific) {
        displayNames[key] = ing.ingredient_name;
      }

      if (map[key]) {
        // Same name + same unit: straightforward sum. Where the per-recipe
        // card rounds what it displays, sum those displayed amounts as exact
        // 1/24ths (see displayed24 above) so the total reconciles with the
        // cards instead of being independently re-rounded from a different,
        // invisible number.
        const q24 = displayed24(ing, unitLower);
        if (q24 !== null) {
          map[key].frac24 = (map[key].frac24 || 0) + q24;
          map[key].quantity = map[key].frac24 / 24;
        } else {
          map[key].quantity = (map[key].quantity || 0) + parseFrac(ing.quantity);
        }
        map[key].rawQuantity = null;
        if (ing.quantity_metric) {
          map[key].quantity_metric = String(
            (Number(map[key].quantity_metric) || 0) + (Number(ing.quantity_metric) || 0)
          );
        }
        map[key].recipeCount += 1;
      } else {
        // Check if this ingredient already exists under a different unit
        const existingKey = Object.keys(map).find((k) => k.startsWith(canon + "@@") && k !== key);
        if (existingKey) {
          const existing = map[existingKey];
          const mergeUpdate = (qty, unit) => {
            existing.quantity = qty;
            existing.rawQuantity = null;
            existing.unit = unit;
            // A cross-unit merge invalidates any exact 1/24ths total tracked
            // for the ingredient's previous unit — recompute it fresh for
            // the new unit rather than carry a stale value into fmtIng.
            existing.frac24 = FRACTION_UNITS.has(unit) ? quantizeTo24(qty) : undefined;
            existing.recipeCount += 1;
            if (!displayNames[existingKey] || isSpecific)
              displayNames[existingKey] = ing.ingredient_name;
          };
          const iUnitNormalized = normalizeCountUnit(ing.unit);

          // Volume merge: cup / tbsp / tsp → tbsp, upgrade back to cup if ≥ 16
          const VOL = { cup: 16, tbsp: 1, tsp: 1 / 3 };
          const eTbsp = VOL[(existing.unit || "").toLowerCase()];
          const iTbsp = VOL[iUnitNormalized];
          if (eTbsp != null && iTbsp != null) {
            const total = existing.quantity * eTbsp + parseFrac(ing.quantity) * iTbsp;
            if (total >= 16) {
              mergeUpdate(total / 16, "cup");
            } else {
              mergeUpdate(total, "tbsp");
            }
            return;
          }
          // Gram merge via density/per-unit tables. Pass the normalized unit so a
          // count-style synonym ("leaf", "piece", ...) is recognized as a plain
          // count (unit "") and can look itself up in G_PER_UNIT, rather than
          // being treated as an unrecognized unit that always fails conversion.
          const existingG = toGrams(existing.quantity, existing.unit, existing.ingredient_name);
          const incomingG = toGrams(ing.quantity, iUnitNormalized, ing.ingredient_name);
          if (existingG !== null && incomingG !== null) {
            existing.quantity = existingG + incomingG;
            existing.rawQuantity = null;
            existing.unit = "g";
            existing.unit_metric = "g";
            existing.quantity_metric = String(existingG + incomingG);
            existing.frac24 = undefined;
            existing.recipeCount += 1;
            if (!displayNames[existingKey] || isSpecific)
              displayNames[existingKey] = ing.ingredient_name;
            return;
          }
          // Conversion not possible — keep as a separate row, but never silently:
          // flag both rows so the UI can surface that this ingredient may be
          // split across two lines, instead of only logging to the console
          // (which no real user ever sees). This is a count-vs-weight mismatch
          // whenever exactly one side is a bare count ("") and the other isn't —
          // regardless of which side is the one missing a G_PER_UNIT entry.
          const eUnit = (existing.unit || "").toLowerCase();
          const mixedCountWeight = (eUnit === "") !== (iUnitNormalized === "");
          if (mixedCountWeight) {
            console.warn(
              `[aggregate] cannot merge count+weight for "${canon}": add g_per_unit to G_PER_UNIT`
            );
            existing.mergeWarning = `May be listed separately from another "${canon}" entry — units couldn't be combined.`;
          }
        }
        const created24 = displayed24(ing, unitLower);
        map[key] = {
          ingredient_name: ing.ingredient_name,
          rawQuantity: ing.quantity,
          quantity: parseFrac(ing.quantity) || 0,
          unit: unitLower,
          quantity_metric: ing.quantity_metric || null,
          unit_metric: ing.unit_metric || null,
          // Agreement data travels with the total: the aggregated line is
          // formatted by the same function as the per-recipe cards, and
          // dropping these here is how "3 cups chicken broth" would come
          // back as "3 cup chicken broth" on the Groceries tab alone.
          countability: ing.countability,
          subunit: ing.subunit,
          recipeCount: 1,
          ...(created24 !== null ? { frac24: created24 } : {}),
          ...(existingKey && map[existingKey]?.mergeWarning
            ? { mergeWarning: map[existingKey].mergeWarning }
            : {}),
        };
      }
    });
  });

  Object.entries(map).forEach(([key, val]) => {
    const canon = key.split("@@")[0];
    val.ingredient_name = MERGE_PREFERRED_DISPLAY[canon] ?? displayNames[key];
  });

  return Object.values(map);
}

/**
 * Aggregate the shopping list, folding in any pantry-side contribution of an
 * ingredient the schedule is already buying.
 *
 * A canonical name can legitimately exist in BOTH pantry.ingredients and
 * pantry.pantry_items — the seed migration alone ships ten of them (salt,
 * sugar, olive oil, cornstarch, cumin, paprika, vinegar, ...) — and nothing
 * stops one recipe from tracking such an item as a counted ingredient while
 * another treats it as an assumed staple. The two sides live in separate
 * tables with separate per-recording increment tables, so they arrive here
 * as two unrelated lists: `active_ingredients`, which becomes the shopping
 * total, and `pantry_items`, which becomes the cupboard section. Nothing
 * used to bring them back together: a schedule with garlic counted as an
 * Ingredient in one recipe and carried as a PantryItem in another totalled
 * only the first recipe's cloves, and the second recipe's contribution
 * rendered as a cupboard staple instead of being added to anything. The
 * shopper gets home a clove short.
 *
 * The rule, stated deliberately: **once any recipe in the schedule counts an
 * item as something you buy, every recipe's use of that item is part of the
 * amount you buy.** Matching pantry rows are folded into the shopping line
 * (through `aggregate` itself, so they take the same same-unit / volume /
 * gram merge paths as any other contribution) and reported back via
 * `foldedPantryIds` so the caller drops them from the cupboard sections
 * rather than showing the same cloves twice.
 *
 * It is asymmetric on purpose: an item no recipe counts stays a staple and
 * never reaches the list. Over-reporting garlic by a clove is harmless;
 * under-reporting it is the defect.
 *
 * Both arguments must already be scaled for the user's serving choices.
 * Returns `{ items, foldedPantryIds }`.
 */
export function aggregateWithPantry(activeIngredients, pantryRows = []) {
  const base = aggregate(activeIngredients);
  const shoppingNames = new Set(base.map((row) => canonical(row.ingredient_name)));

  // A zero/blank pantry quantity has nothing to contribute and would only
  // disappear from the cupboard if folded, so leave those where they are.
  const folded = (pantryRows || []).filter(
    (p) => shoppingNames.has(canonical(p.name || "")) && parseFrac(p.quantity) > 0
  );
  if (folded.length === 0) return { items: base, foldedPantryIds: new Set() };

  const asIngredients = folded.map((p) => ({
    ingredient_name: p.name,
    quantity: p.quantity,
    unit: p.unit,
    quantity_metric: p.quantity_metric,
    unit_metric: p.unit_metric,
    countability: p.countability,
    subunit: p.subunit,
  }));

  return {
    items: aggregate([...activeIngredients, { ingredients: asIngredients }]),
    foldedPantryIds: new Set(folded.map((p) => p.id)),
  };
}
